import { createColumnHelper } from '@tanstack/react-table';

import { Order } from 'types/order';
import { SKU } from 'types/sku';
import { Status } from 'types/items';
import { convertUTCToLocal } from 'utils/dateUtils';

export type SkuItemsById = Record<string, number | undefined>;

const col = createColumnHelper<Order>();

/** Single-SKU requests resolve that detail's SKU id; multi-SKU rows use ''. */
const singleSkuId = (order: Order) => {
  const details = order?.details;
  return details?.length === 1 ? details[0]?.sku : '';
};

/** Single-SKU rows show that SKU's case count (or ' - '); multi-SKU the sum. */
const caseAmount = (skuID: string, order: Order) => {
  if (skuID) {
    return (
      order.details.find((skuCaseId) => skuCaseId.sku === skuID)?.casesCount ||
      ' - '
    );
  }

  const sum = order?.details?.reduce((acc, d) => d?.casesCount + acc, 0);

  return sum;
};

export const buildRequestColumns = (
  skuItemsById: SkuItemsById,
  skuItems: SKU[],
  skuPackages: SKU[],
) => {
  /** Looks the SKU name up across both item and package SKUs. */
  const getSkuNameById = (skuID?: string) => {
    let skuNameById = '';
    if (!Boolean(skuID)) return (skuNameById = ' - ');

    const skuName = [...skuItems, ...skuPackages].find(
      (_sku) => _sku._id === skuID,
    );
    skuNameById = skuName?.name || ' - ';
    return skuNameById;
  };

  return [
    col.accessor('orderNumber', {
      header: 'Order No',
      minSize: 70,
      cell: (info) => info.getValue() || '-',
    }),
    col.accessor(
      (order) => {
        const length = order?.details?.length;
        return length > 1 ? `2+ SKU's` : getSkuNameById(singleSkuId(order));
      },
      { id: 'skuName', header: 'SKU name', minSize: 150 },
    ),
    col.accessor((order) => convertUTCToLocal(order?.deliveryAt, true, true), {
      id: 'deliveryAt',
      header: 'Delivery Date',
      minSize: 150,
    }),
    col.accessor((order) => convertUTCToLocal(order?.createdAt, true, true), {
      id: 'createdAt',
      header: 'Requested Date',
      minSize: 150,
    }),
    col.accessor((order) => order?.site?.name || '-', {
      id: 'site',
      header: 'Site',
      minSize: 160,
    }),
    col.accessor((order) => caseAmount(singleSkuId(order), order), {
      id: 'caseAmount',
      header: 'Case Amount',
      minSize: 100,
      meta: { align: 'center' },
    }),
    col.accessor((order) => Status[order?.status || 0], {
      id: 'status',
      header: 'Status',
      minSize: 110,
      meta: { align: 'center' },
    }),
    col.accessor(
      (order) =>
        order?.details.reduce((skuAdded, orderDetails) => {
          const itemsQtyInCase = skuItemsById[orderDetails.sku] ?? 1;
          return skuAdded + orderDetails.casesCount * itemsQtyInCase;
        }, 0),
      {
        id: 'cupsCount',
        header: 'Cups Count',
        minSize: 90,
        meta: { align: 'center' },
      },
    ),
  ];
};
